import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { Fragment, useEffect, useMemo, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import LoyaltyAccountLandingPageBenefitItem from './LoyaltyAccountLandingPageBenefitItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Divider from 'LuxKit/Divider'
import { useLocation } from 'react-router'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'

const Section = styled(Pane)`
  padding: ${rem(24)};
  /* Offset for sticky header */
  scroll-margin-top: 80px;

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }
`

function LoyaltyAccountLandingPageYourBenefits() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const account = useAppSelector(state => state.loyalty.account.data)
  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const [, tierName] = useCurrentLoyaltyTier()

  // phone support always in the end
  const visibleBenefits = useMemo(() => {
    if (!account?.benefits) return []
    return account.benefits
      .filter(benefit => {
        if (benefit.code === 'esim_package' && !isEsimEnabled) return false
        return true
      })
      .sort((a, b) => {
        if (a.code === 'phone_support') return 1
        if (b.code === 'phone_support') return -1
        return 0
      })
  }, [account?.benefits, isEsimEnabled])

  useEffect(() => {
    if (location.hash === '#benefits-your-tier' && sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [location.hash])

  if (visibleBenefits.length === 0) return null

  return (
    <Section ref={sectionRef} id="benefits-your-tier">
      <VerticalSpacer gap={12}>
        <VerticalSpacer gap={2}>
          <Heading variant="heading3">
            <FormattedMessage
              id="WW99qMoXTKPgbZgJ5nBr"
              defaultMessage="Your {tierName} <i>benefits</i>"
              description="Heading for user's current loyalty tier benefits section"
              values={{ tierName, i: (chunks) => <i>{chunks}</i> }}
            />
          </Heading>
          {!!account?.statusReviewDate && <BodyText variant="medium">
            <FormattedMessage
              id="NA3WzkfvTsEuFv-49rdB"
              defaultMessage="Expire {date}"
              description="Expiry date for loyalty tier benefits in the loyalty program section"
              values={{ date: <FormatDateTime value={account.statusReviewDate} format="dmy-casual" /> }}
            />
          </BodyText>}
        </VerticalSpacer>
        <VerticalSpacer gap={8}>
          {visibleBenefits.map((benefit, index) => <Fragment key={benefit.code}>
            {index !== 0 && <Divider kind="secondary" />}
            <LoyaltyAccountLandingPageBenefitItem
              key={benefit.code}
              benefit={benefit}
            />
          </Fragment>)}
        </VerticalSpacer>
      </VerticalSpacer>
    </Section>
  )
}

export default LoyaltyAccountLandingPageYourBenefits
